import type { AuthUser } from "@/types/auth";

export const TEMPLATE_ADMIN_ROLES = [
  "SUPER_ADMIN",
  "ADMIN",
  "FOUNDER",
] as const;

export const ADMIN_ROLES = [
  ...TEMPLATE_ADMIN_ROLES,
  "CUSTOMER_CARE",
  "ACCOUNTANT",
] as const;

export const ACCOUNTANT_ROLES = ["ACCOUNTANT"] as const;
export const DOCUMENT_GENERATION_ROLES = [...ADMIN_ROLES, ...ACCOUNTANT_ROLES] as const;
export const ADMIN_PORTAL_ROLES = [...DOCUMENT_GENERATION_ROLES];
export const PARTNER_ROLES = ["PARTNER"] as const;
const AUTH_ROUTE_PREFIXES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
];

export function readCsrfTokenCookie() {
  if (typeof document === "undefined") {
    return null;
  }

  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find(
      (part) =>
        part.startsWith("__Host-landnest_csrf=") ||
        part.startsWith("landnest_csrf="),
    );

  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.split("=")[1] ?? "");
}

export function getDefaultRouteForUser(user: AuthUser) {
  if (ACCOUNTANT_ROLES.includes(user.role as any)) {
    return "/accountant";
  }

  // Founders get a deliberately simple landing: just the sales awaiting their
  // receipt upload + approve/reject. They still have access to every other
  // admin page from the sidebar.
  if (user.role === "FOUNDER") {
    return "/admin/founder-review";
  }

  if (ADMIN_ROLES.includes(user.role as any)) {
    return "/admin/dashboard";
  }

  if (PARTNER_ROLES.includes(user.role as any)) {
    return "/partner/home";
  }

  return "/profile";
}

export function isTemplateAdminRole(role: string | null | undefined) {
  return Boolean(role && TEMPLATE_ADMIN_ROLES.includes(role as (typeof TEMPLATE_ADMIN_ROLES)[number]));
}

export function canGenerateDocumentsRole(role: string | null | undefined) {
  return Boolean(
    role && DOCUMENT_GENERATION_ROLES.includes(role as (typeof DOCUMENT_GENERATION_ROLES)[number]),
  );
}

export function canManageRefundsUser(user: AuthUser | null | undefined) {
  if (!user || !["SUPER_ADMIN", "FOUNDER", "ADMIN", "ACCOUNTANT"].includes(user.role)) {
    return false;
  }

  return (
    user.role === "SUPER_ADMIN" ||
    user.role === "FOUNDER" ||
    user.rolePermissions?.canRejectPayments === true
  );
}

function safeDecodeURIComponent(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function isAuthRoutePath(pathname: string) {
  return AUTH_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}?`) || pathname.startsWith(`${prefix}#`),
  );
}

export function sanitizeRedirectPath(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const decoded = safeDecodeURIComponent(value).trim();
  if (!decoded.startsWith("/") || decoded.startsWith("//")) {
    return null;
  }

  if (isAuthRoutePath(decoded)) {
    return null;
  }

  return decoded;
}

export function buildLoginRedirect(pathname: string, search = "", hash = "") {
  return encodeURIComponent(`${pathname}${search}${hash}`);
}

export function isAuthorized(user: AuthUser | null, roles?: readonly string[]) {
  if (!user) {
    return false;
  }

  if (!roles || roles.length === 0) {
    return true;
  }

  return roles.includes(user.role);
}
